// WSR File Storage Service
// Handles file uploads, storage, and URL generation using Supabase Storage

import { createClient } from '@supabase/supabase-js';

interface FileUploadResult {
    fileUrl: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    filePath: string;
}

class WSRFileStorageService {
    private static instance: WSRFileStorageService;
    private supabase: any;
    private bucketName: string = 'wsr-files';

    constructor() {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        if (supabaseUrl && supabaseKey) {
            this.supabase = createClient(supabaseUrl, supabaseKey);
        } else {
            console.warn('Supabase credentials not found, using fallback storage');
        }
    }

    static getInstance(): WSRFileStorageService {
        if (!WSRFileStorageService.instance) {
            WSRFileStorageService.instance = new WSRFileStorageService();
        }
        return WSRFileStorageService.instance;
    }

    // Upload original WSR file (PDF, MD, TXT) to Supabase Storage
    async uploadOriginalFile(file: File, clientId: number, wsrId?: number): Promise<FileUploadResult> {
        if (!this.supabase) {
            return this.createLocalFileReference(file);
        }

        try {
            // Create a unique file path
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const randomId = Math.random().toString(36).substring(2, 15);
            const fileExtension = file.name.split('.').pop();
            const fileName = `${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
            const filePath = `clients/${clientId}/wsr-originals/${timestamp}_${randomId}_${fileName}`;

            // Upload to Supabase Storage
            const { data, error } = await this.supabase.storage
                .from(this.bucketName)
                .upload(filePath, file, {
                    cacheControl: '3600',
                    upsert: false
                });

            if (error) {
                throw new Error(`Supabase upload failed: ${error.message}`);
            }

            // Get public URL
            const { data: urlData } = this.supabase.storage
                .from(this.bucketName)
                .getPublicUrl(filePath);

            return {
                fileUrl: urlData.publicUrl,
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                filePath: filePath
            };
        } catch (error) {
            console.warn('Supabase upload failed, using local storage:', error);
            return this.createLocalFileReference(file);
        }
    }

    // Generate and upload PDF from WSR data to Supabase
    async generateAndUploadPDF(wsrData: any, clientId: number): Promise<FileUploadResult> {
        if (!this.supabase) {
            return this.createMockPDFReference(wsrData);
        }

        try {
            // Generate PDF blob (you can use jsPDF or similar)
            const pdfBlob = await this.generatePDFBlob(wsrData);

            // Create file path for published PDF
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const fileName = `WSR_${wsrData.client_name}_${wsrData.week_ending}.pdf`;
            const filePath = `clients/${clientId}/wsr-published/${timestamp}_${fileName}`;

            // Upload PDF to Supabase
            const { data, error } = await this.supabase.storage
                .from(this.bucketName)
                .upload(filePath, pdfBlob, {
                    contentType: 'application/pdf',
                    cacheControl: '3600',
                    upsert: true // Allow overwriting
                });

            if (error) {
                throw new Error(`PDF upload failed: ${error.message}`);
            }

            // Get public URL
            const { data: urlData } = this.supabase.storage
                .from(this.bucketName)
                .getPublicUrl(filePath);

            return {
                fileUrl: urlData.publicUrl,
                fileName: fileName,
                fileSize: pdfBlob.size,
                fileType: 'application/pdf',
                filePath: filePath
            };
        } catch (error) {
            console.warn('PDF generation failed:', error);
            return this.createMockPDFReference(wsrData);
        }
    }

    // Delete file from Supabase Storage
    async deleteFile(filePath: string): Promise<boolean> {
        if (!this.supabase) {
            return false;
        }

        try {
            const { error } = await this.supabase.storage
                .from(this.bucketName)
                .remove([filePath]);

            return !error;
        } catch (error) {
            console.error('File deletion failed:', error);
            return false;
        }
    }

    // Get signed URL for private files (if needed)
    async getSignedUrl(filePath: string, expiresIn: number = 3600): Promise<string | null> {
        if (!this.supabase) {
            return null;
        }

        try {
            const { data, error } = await this.supabase.storage
                .from(this.bucketName)
                .createSignedUrl(filePath, expiresIn);

            if (error) {
                throw new Error(`Signed URL generation failed: ${error.message}`);
            }

            return data.signedUrl;
        } catch (error) {
            console.error('Signed URL generation failed:', error);
            return null;
        }
    }

    // Initialize storage bucket (call this once during app setup)
    async initializeBucket(): Promise<boolean> {
        if (!this.supabase) {
            return false;
        }

        try {
            // Check if bucket exists
            const { data: buckets, error: listError } = await this.supabase.storage.listBuckets();

            if (listError) {
                throw new Error(`Failed to list buckets: ${listError.message}`);
            }

            const bucketExists = buckets.some((bucket: any) => bucket.name === this.bucketName);

            if (!bucketExists) {
                // Create bucket
                const { error: createError } = await this.supabase.storage.createBucket(this.bucketName, {
                    public: true,
                    allowedMimeTypes: ['application/pdf', 'text/markdown', 'text/plain'],
                    fileSizeLimit: 10485760 // 10MB
                });

                if (createError) {
                    throw new Error(`Failed to create bucket: ${createError.message}`);
                }

                console.log(`Created Supabase storage bucket: ${this.bucketName}`);
            }

            return true;
        } catch (error) {
            console.error('Bucket initialization failed:', error);
            return false;
        }
    }

    // Generate PDF blob from WSR data
    private async generatePDFBlob(wsrData: any): Promise<Blob> {
        // Import jsPDF dynamically
        const { jsPDF } = await import('jspdf');

        const doc = new jsPDF();

        // Add WSR content to PDF
        doc.setFontSize(16);
        doc.text('Weekly Status Report', 20, 20);

        doc.setFontSize(12);
        doc.text(`Week Ending: ${wsrData.week_ending}`, 20, 35);
        doc.text(`Prepared By: ${wsrData.prepared_by}`, 20, 45);
        doc.text(`Project Sponsor: ${wsrData.project_sponsor}`, 20, 55);

        let yPos = 70;

        // Report Summary
        if (wsrData.report_summary) {
            doc.setFontSize(14);
            doc.text('Report Summary', 20, yPos);
            yPos += 10;
            doc.setFontSize(10);
            const summaryLines = doc.splitTextToSize(wsrData.report_summary, 170);
            doc.text(summaryLines, 20, yPos);
            yPos += summaryLines.length * 5 + 10;
        }

        // Work Accomplished
        if (wsrData.accomplished && wsrData.accomplished.length > 0) {
            doc.setFontSize(14);
            doc.text('Work Accomplished', 20, yPos);
            yPos += 10;
            doc.setFontSize(10);

            wsrData.accomplished.forEach((item: any, index: number) => {
                doc.text(`${index + 1}. ${item.task}`, 25, yPos);
                yPos += 7;
                if (item.notes) {
                    const noteLines = doc.splitTextToSize(`   ${item.notes}`, 165);
                    doc.text(noteLines, 25, yPos);
                    yPos += noteLines.length * 5;
                }
                yPos += 3;
            });
        }

        // Convert to blob
        const pdfBlob = doc.output('blob');
        return pdfBlob;
    }

    // Fallback: Create local file reference for development
    private createLocalFileReference(file: File): FileUploadResult {
        const blobUrl = URL.createObjectURL(file);

        // Store in localStorage for persistence
        const fileKey = `wsr_file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const fileInfo = {
            url: blobUrl,
            name: file.name,
            size: file.size,
            type: file.type,
            timestamp: Date.now()
        };

        localStorage.setItem(fileKey, JSON.stringify(fileInfo));

        return {
            fileUrl: blobUrl,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            filePath: fileKey
        };
    }

    // Create mock PDF reference for development
    private createMockPDFReference(wsrData: any): FileUploadResult {
        const fileName = `WSR_${wsrData.client_name}_${wsrData.week_ending}.pdf`;
        return {
            fileUrl: '#pdf-not-generated',
            fileName: fileName,
            fileSize: 0,
            fileType: 'application/pdf',
            filePath: `mock/${fileName}`
        };
    }

    // Clean up old local file references
    cleanupLocalFiles(olderThanHours: number = 24): void {
        const cutoffTime = Date.now() - (olderThanHours * 60 * 60 * 1000);

        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('wsr_file_')) {
                try {
                    const fileInfo = JSON.parse(localStorage.getItem(key) || '{}');
                    if (fileInfo.timestamp && fileInfo.timestamp < cutoffTime) {
                        if (fileInfo.url && fileInfo.url.startsWith('blob:')) {
                            URL.revokeObjectURL(fileInfo.url);
                        }
                        localStorage.removeItem(key);
                    }
                } catch (error) {
                    localStorage.removeItem(key);
                }
            }
        });
    }

    // Download file
    async downloadFile(fileUrl: string, fileName: string): Promise<void> {
        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();

            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('File download failed:', error);
            window.open(fileUrl, '_blank');
        }
    }
}

export default WSRFileStorageService;